export const growthServices = [
  {
    slug: "ga4-digital-analytics",
    icon: "BarChart3",
    es: {
      name: "Medición & Digital Analytics",
      tagline: "GA4, GTM y server-side tagging: una medición en la que puedes confiar.",
      intro: "Gran parte de las implementaciones de GA4 que auditamos miden mal. Y sin datos fiables no hay growth: implementamos, auditamos y gobernamos tu stack de medición para que cada decisión se apoye en números reales.",
      blocks: [
        {
          h: "Auditoría de medición",
          p: "Revisamos tu GA4, tu GTM y tus píxeles de paid media con lupa: eventos duplicados, conversiones mal configuradas y consentimiento que rompe la medición.",
          list: ["Auditoría completa de GA4 y GTM", "Detección de eventos duplicados o perdidos", "Revisión de píxeles de paid media"],
        },
        {
          h: "Implementación & migración",
          p: "Re-implementamos GA4 con un plan de medición alineado a tu negocio: cada evento existe porque responde a una pregunta concreta.",
          list: ["Plan de medición orientado a KPIs", "GTM web & server-side tagging", "Migraciones desde Universal Analytics"],
        },
        {
          h: "Privacidad & Consent Mode",
          p: "Medición respetuosa con la privacidad que no deja ciegos tus canales: Consent Mode v2, modelado de conversiones y first-party data.",
          list: ["Consent Mode v2 avanzado", "Server-side para recuperar señal", "Cumplimiento RGPD sin perder datos"],
        },
        {
          h: "Calidad & gobierno",
          p: "La medición se degrada con cada release. Implantamos QA automatizado y gobierno del dato para que no vuelva a pasar.",
          list: ["QA automatizado de eventos", "Documentación y naming conventions", "Alertas de rotura de medición"],
        },
      ],
      outcomes: ["Medición fiable en GA4 y paid media", "Hasta un 30% más de conversiones visibles con server-side", "Cumplimiento RGPD sin perder señal", "Un único plan de medición para todo el equipo"],
    },
    en: {
      name: "Measurement & Digital Analytics",
      tagline: "GA4, GTM and server-side tagging: measurement you can trust.",
      intro: "A large share of the GA4 setups we audit measure wrong. And without reliable data there's no growth: we implement, audit and govern your measurement stack so every decision rests on real numbers.",
      blocks: [
        {
          h: "Measurement audit",
          p: "We put your GA4, GTM and paid media pixels under the microscope: duplicated events, misconfigured conversions and consent setups that break measurement.",
          list: ["Full GA4 and GTM audit", "Detection of duplicated or lost events", "Paid media pixel review"],
        },
        {
          h: "Implementation & migration",
          p: "We re-implement GA4 with a measurement plan aligned to your business: every event exists because it answers a concrete question.",
          list: ["KPI-driven measurement plan", "GTM web & server-side tagging", "Migrations from Universal Analytics"],
        },
        {
          h: "Privacy & Consent Mode",
          p: "Privacy-respectful measurement that doesn't blind your channels: Consent Mode v2, conversion modeling and first-party data.",
          list: ["Advanced Consent Mode v2", "Server-side to recover signal", "GDPR compliance without losing data"],
        },
        {
          h: "Quality & governance",
          p: "Measurement degrades with every release. We put automated QA and data governance in place so it never happens again.",
          list: ["Automated event QA", "Documentation and naming conventions", "Measurement breakage alerts"],
        },
      ],
      outcomes: ["Reliable measurement in GA4 and paid media", "Up to 30% more visible conversions with server-side", "GDPR compliance without losing signal", "One measurement plan for the whole team"],
    },
    deep: {
      es: {
        kicker: "Metodología de medición",
        title: "De datos sospechosos a decisiones seguras",
        intro: "Una buena medición no se improvisa: se diseña, se implementa y se cuida. Este es el proceso con el que convertimos tu GA4 en la fuente de verdad del equipo.",
        caps: [
          { t: "Auditoría sin piedad", d: "Cada evento, cada conversión y cada píxel, revisado contra lo que tu negocio necesita medir." },
          { t: "Plan de medición", d: "KPIs, eventos y parámetros definidos antes de tocar una sola etiqueta." },
          { t: "Server-side first", d: "GTM server-side y first-party data para recuperar la señal que el navegador ya no da." },
          { t: "Gobierno continuo", d: "QA automatizado y documentación viva: la medición no se rompe con cada release." },
        ],
        visual: {
          label: "El camino, semana a semana",
          phases: [
            { t: "Auditoría & plan de medición", w: "Semanas 1–2", d: "Revisión completa del stack y definición de KPIs, eventos y conversiones." },
            { t: "Implementación", w: "Semanas 3–5", d: "GA4, GTM web y server-side configurados según el plan de medición." },
            { t: "QA & validación", w: "Semana 6", d: "Testing automatizado, comparativa con datos reales y Consent Mode verificado." },
            { t: "Gobierno & formación", w: "Semana 7+", d: "Documentación, alertas de calidad y tu equipo formado para no depender de nadie." },
          ],
        },
        steps: [
          { n: "01", t: "Escuchamos", d: "Entendemos tu modelo de negocio y qué preguntas debe responder la analítica." },
          { n: "02", t: "Auditamos", d: "Radiografía completa de tu medición actual: qué sobra, qué falta y qué miente." },
          { n: "03", t: "Diseñamos", d: "Plan de medición con eventos, parámetros y conversiones alineados a tus KPIs." },
          { n: "04", t: "Implementamos", d: "GA4, GTM y server-side tagging con QA en cada release." },
          { n: "05", t: "Gobernamos", d: "Alertas, documentación y formación para que la calidad se mantenga." },
        ],
        closing: { title: "Si no mides bien, estás decidiendo a ciegas.", body: "Medición fiable, privacidad cumplida y un equipo que por fin confía en sus números.", cta: "Audita mi medición" },
      },
      en: {
        kicker: "Measurement methodology",
        title: "From suspicious data to confident decisions",
        intro: "Good measurement isn't improvised: it's designed, implemented and cared for. This is the process that turns your GA4 into the team's source of truth.",
        caps: [
          { t: "Ruthless audit", d: "Every event, every conversion and every pixel, checked against what your business needs to measure." },
          { t: "Measurement plan", d: "KPIs, events and parameters defined before touching a single tag." },
          { t: "Server-side first", d: "Server-side GTM and first-party data to recover the signal browsers no longer provide." },
          { t: "Continuous governance", d: "Automated QA and living documentation: measurement doesn't break with every release." },
        ],
        visual: {
          label: "The road, week by week",
          phases: [
            { t: "Audit & measurement plan", w: "Weeks 1–2", d: "Full stack review and definition of KPIs, events and conversions." },
            { t: "Implementation", w: "Weeks 3–5", d: "GA4, web GTM and server-side configured per the measurement plan." },
            { t: "QA & validation", w: "Week 6", d: "Automated testing, comparison against real data and verified Consent Mode." },
            { t: "Governance & training", w: "Week 7+", d: "Documentation, quality alerts and your team trained to be self-sufficient." },
          ],
        },
        steps: [
          { n: "01", t: "We listen", d: "We understand your business model and the questions analytics must answer." },
          { n: "02", t: "We audit", d: "A full X-ray of your current measurement: what's redundant, what's missing and what's lying." },
          { n: "03", t: "We design", d: "A measurement plan with events, parameters and conversions aligned to your KPIs." },
          { n: "04", t: "We implement", d: "GA4, GTM and server-side tagging with QA on every release." },
          { n: "05", t: "We govern", d: "Alerts, documentation and training so quality endures." },
        ],
        closing: { title: "If you measure wrong, you're deciding blind.", body: "Reliable measurement, privacy compliance and a team that finally trusts its numbers.", cta: "Audit my measurement" },
      },
    },
  },
  {
    slug: "crm-lifecycle",
    icon: "MousePointerClick",
    es: {
      name: "Activación & CRM",
      tagline: "Customer journeys, CRO y lifecycle marketing que convierten datos en ingresos.",
      intro: "Tener datos no sirve de nada si no se activan. Diseñamos journeys, optimizamos la conversión y construimos programas de lifecycle que retienen clientes y los hacen crecer.",
      blocks: [
        {
          h: "Customer Journey mapping",
          p: "Mapeamos el viaje real de tus clientes — no el que dibuja el PowerPoint — con datos de cada touchpoint: web, email, ads, producto y soporte.",
          list: ["Mapa de touchpoints con datos reales", "Detección de fricciones y fugas", "Momentos clave de activación"],
        },
        {
          h: "CRO — Conversion Rate Optimization",
          p: "Programa continuo de experimentación: hipótesis priorizadas por impacto, tests A/B con rigor estadístico y aprendizaje acumulado.",
          list: ["Auditoría de conversión y heurísticas", "Roadmap de experimentación A/B", "Tests con rigor estadístico"],
        },
        {
          h: "Estrategia CRM & lifecycle",
          p: "Del primer email a la recompra: segmentos, automatizaciones y mensajes que llegan justo cuando el cliente los necesita.",
          list: ["Segmentación y arquitectura de campañas", "Automatizaciones de lifecycle", "Programas de fidelización y recompra"],
        },
      ],
      outcomes: ["Journeys basados en datos reales", "Ratio de conversión en mejora continua", "Automatizaciones que venden mientras duermes", "Clientes que repiten y recomiendan"],
    },
    en: {
      name: "Activation & CRM",
      tagline: "Customer journeys, CRO and lifecycle marketing that turn data into revenue.",
      intro: "Having data is useless if it never gets activated. We design journeys, optimize conversion and build lifecycle programs that retain customers and grow them.",
      blocks: [
        {
          h: "Customer Journey mapping",
          p: "We map your customers' real journey — not the one in the slide deck — with data from every touchpoint: web, email, ads, product and support.",
          list: ["Touchpoint mapping with real data", "Friction and leak detection", "Key activation moments"],
        },
        {
          h: "CRO — Conversion Rate Optimization",
          p: "A continuous experimentation program: hypotheses prioritized by impact, statistically sound A/B tests and compounding learnings.",
          list: ["Conversion and heuristics audit", "A/B experimentation roadmap", "Statistically rigorous testing"],
        },
        {
          h: "CRM & lifecycle strategy",
          p: "From the first email to the repeat purchase: segments, automations and messages that land exactly when the customer needs them.",
          list: ["Segmentation and campaign architecture", "Lifecycle automations", "Loyalty and repurchase programs"],
        },
      ],
      outcomes: ["Journeys built on real data", "Conversion rate in continuous improvement", "Automations that sell while you sleep", "Customers who come back and refer"],
    },
    deep: {
      es: {
        kicker: "Del dato a la acción",
        title: "La activación es donde el dato se convierte en ingreso",
        intro: "La mayoría de empresas acumulan datos de cliente que nadie usa. Nosotros los convertimos en journeys, experimentos y automatizaciones que mueven la aguja.",
        caps: [
          { t: "Journeys con datos reales", d: "Cada touchpoint medido: sabemos dónde se pierde el cliente y por qué." },
          { t: "Experimentación seria", d: "Hipótesis priorizadas por impacto y tests con rigor estadístico. Nada de opinions." },
          { t: "Lifecycle automatizado", d: "El mensaje correcto, en el canal correcto, en el momento correcto. Sin intervención manual." },
          { t: "Aprendizaje acumulado", d: "Cada test deja conocimiento documentado que hace más inteligente al siguiente." },
        ],
        visual: {
          label: "Antes / después en programas de activación",
          legendBefore: "Antes",
          legendAfter: "Después",
          metrics: [
            { label: "Conversión de checkout", before: "1,8 %", after: "2,9 %", beforePct: 100, afterPct: 62 },
            { label: "Leads que se convierten en cliente", before: "6 %", after: "11 %", beforePct: 55, afterPct: 100 },
            { label: "Ingresos por email enviado", before: "0,12 €", after: "0,41 €", beforePct: 30, afterPct: 100 },
          ],
          note: "Ejemplos representativos de programas de CRO y lifecycle marketing.",
        },
        steps: [
          { n: "01", t: "Mapeamos", d: "El journey real de tus clientes con los datos de cada touchpoint." },
          { n: "02", t: "Priorizamos", d: "Hipótesis ordenadas por impacto potencial y esfuerzo." },
          { n: "03", t: "Experimentamos", d: "Tests A/B con rigor estadístico y aprendizaje documentado." },
          { n: "04", t: "Automatizamos", d: "Lifecycle y CRM que trabajan solos, segmento a segmento." },
          { n: "05", t: "Escalamos", d: "Lo que funciona, a todos los canales y mercados." },
        ],
        closing: { title: "Tus datos ya saben cómo vender más. Nosotros los escuchamos.", body: "Journeys reales, experimentación continua y lifecycle marketing que retiene.", cta: "Activa mis datos" },
      },
      en: {
        kicker: "From data to action",
        title: "Activation is where data turns into revenue",
        intro: "Most companies pile up customer data nobody uses. We turn it into journeys, experiments and automations that move the needle.",
        caps: [
          { t: "Journeys with real data", d: "Every touchpoint measured: we know where the customer drops off and why." },
          { t: "Serious experimentation", d: "Hypotheses prioritized by impact and statistically sound tests. No opinions." },
          { t: "Automated lifecycle", d: "The right message, on the right channel, at the right moment. No manual work." },
          { t: "Compounding learnings", d: "Every test leaves documented knowledge that makes the next one smarter." },
        ],
        visual: {
          label: "Before / after in activation programs",
          legendBefore: "Before",
          legendAfter: "After",
          metrics: [
            { label: "Checkout conversion", before: "1.8%", after: "2.9%", beforePct: 100, afterPct: 62 },
            { label: "Leads converting to customers", before: "6%", after: "11%", beforePct: 55, afterPct: 100 },
            { label: "Revenue per email sent", before: "€0.12", after: "€0.41", beforePct: 30, afterPct: 100 },
          ],
          note: "Representative examples from CRO and lifecycle marketing programs.",
        },
        steps: [
          { n: "01", t: "We map", d: "Your customers' real journey using data from every touchpoint." },
          { n: "02", t: "We prioritize", d: "Hypotheses ranked by potential impact and effort." },
          { n: "03", t: "We experiment", d: "A/B tests with statistical rigor and documented learnings." },
          { n: "04", t: "We automate", d: "Lifecycle and CRM that work on their own, segment by segment." },
          { n: "05", t: "We scale", d: "What works gets rolled out across channels and markets." },
        ],
        closing: { title: "Your data already knows how to sell more. We listen to it.", body: "Real journeys, continuous experimentation and lifecycle marketing that retains.", cta: "Activate my data" },
      },
    },
  },
  {
    slug: "bigquery-dashboarding",
    icon: "DatabaseZap",
    es: {
      name: "Data & Dashboarding",
      tagline: "BigQuery, Power BI y Looker: un único punto de verdad para todo el equipo.",
      intro: "Si cada departamento tiene sus propios números, nadie tiene la razón. Construimos tu data warehouse de marketing y los dashboards que todo el equipo consulta cada mañana.",
      blocks: [
        {
          h: "BigQuery & data warehousing",
          p: "Centralizamos tus fuentes de marketing, ventas y producto en BigQuery: pipelines automatizados, datos modelados y coste bajo control.",
          list: ["Pipelines desde GA4, Ads, CRM y más", "Modelado de datos para analítica", "Coste de BigQuery optimizado"],
        },
        {
          h: "Dashboards que se usan",
          p: "Diseñamos dashboards en Power BI y Looker que la gente abre a diario: claros, rápidos y con las métricas que importan.",
          list: ["Power BI & Looker Studio", "Diseño orientado a decisión", "Métricas certificadas y documentadas"],
        },
        {
          h: "Un único punto de verdad",
          p: "Definiciones de métricas acordadas y gobernadas para que marketing, ventas y dirección hablen por fin el mismo idioma.",
          list: ["Capa semántica de métricas", "Gobierno y calidad del dato", "Formación y adopción por equipos"],
        },
      ],
      outcomes: ["Un único punto de verdad para marketing", "Reporting automatizado: adiós al Excel del lunes", "Dashboards con adopción real", "Decisiones más rápidas en todos los niveles"],
    },
    en: {
      name: "Data & Dashboarding",
      tagline: "BigQuery, Power BI and Looker: a single source of truth for the whole team.",
      intro: "If every department has its own numbers, nobody is right. We build your marketing data warehouse and the dashboards the whole team checks every morning.",
      blocks: [
        {
          h: "BigQuery & data warehousing",
          p: "We centralize your marketing, sales and product sources in BigQuery: automated pipelines, modeled data and costs under control.",
          list: ["Pipelines from GA4, Ads, CRM and more", "Data modeling for analytics", "Optimized BigQuery costs"],
        },
        {
          h: "Dashboards people actually use",
          p: "We design Power BI and Looker dashboards people open daily: clear, fast and focused on the metrics that matter.",
          list: ["Power BI & Looker Studio", "Decision-oriented design", "Certified, documented metrics"],
        },
        {
          h: "A single source of truth",
          p: "Agreed, governed metric definitions so marketing, sales and leadership finally speak the same language.",
          list: ["Semantic metrics layer", "Data governance and quality", "Team training and adoption"],
        },
      ],
      outcomes: ["A single source of truth for marketing", "Automated reporting: goodbye to Monday's Excel", "Dashboards with real adoption", "Faster decisions at every level"],
    },
    deep: {
      es: {
        kicker: "Ingeniería del reporting",
        title: "El lunes ya nadie pregunta de dónde salen los números",
        intro: "El reporting manual roba horas y siembra desconfianza. Centralizamos tus datos en BigQuery, certificamos las métricas y construimos dashboards que el equipo adopta de verdad.",
        caps: [
          { t: "Todas las fuentes", d: "GA4, Google Ads, Meta, CRM y producto fluyendo a BigQuery sin intervención manual." },
          { t: "Datos modelados", d: "Limpios, unidos y listos para analizar — no un lago de tablas crudas." },
          { t: "Métricas certificadas", d: "Una definición única y gobernada para cada KPI. Fin del debate de cifras." },
          { t: "Adopción real", d: "Diseño orientado a decisión y formación por equipos. Dashboards que se abren a diario." },
        ],
        visual: {
          label: "Madurez objetivo del stack de reporting",
          pillars: [
            { n: "Fuentes conectadas", v: 95 },
            { n: "Frescura de datos", v: 90 },
            { n: "Adopción del equipo", v: 85 },
            { n: "Automatización del reporting", v: 92 },
          ],
          note: "Objetivo típico de madurez tras el proyecto de data warehouse de marketing.",
        },
        steps: [
          { n: "01", t: "Conectamos", d: "Fuentes de marketing, ventas y producto fluyendo a BigQuery." },
          { n: "02", t: "Modelamos", d: "Datos limpios, unidos y listos para analizar." },
          { n: "03", t: "Definimos", d: "Métricas certificadas y una capa semántica común." },
          { n: "04", t: "Visualizamos", d: "Dashboards en Power BI y Looker que se usan a diario." },
          { n: "05", t: "Adoptamos", d: "Formación, rituales y soporte para que el cambio se quede." },
        ],
        closing: { title: "Un solo número. Una sola verdad.", body: "Data warehouse en BigQuery, métricas gobernadas y dashboards con adopción real.", cta: "Unifica mi reporting" },
      },
      en: {
        kicker: "Reporting engineering",
        title: "On Monday, nobody asks where the numbers came from",
        intro: "Manual reporting steals hours and breeds distrust. We centralize your data in BigQuery, certify the metrics and build dashboards the team truly adopts.",
        caps: [
          { t: "Every source", d: "GA4, Google Ads, Meta, CRM and product flowing into BigQuery with zero manual work." },
          { t: "Modeled data", d: "Clean, joined and ready to analyze — not a lake of raw tables." },
          { t: "Certified metrics", d: "A single, governed definition for every KPI. The end of number debates." },
          { t: "Real adoption", d: "Decision-oriented design and team training. Dashboards opened daily." },
        ],
        visual: {
          label: "Target maturity of the reporting stack",
          pillars: [
            { n: "Connected sources", v: 95 },
            { n: "Data freshness", v: 90 },
            { n: "Team adoption", v: 85 },
            { n: "Reporting automation", v: 92 },
          ],
          note: "Typical maturity target after the marketing data warehouse project.",
        },
        steps: [
          { n: "01", t: "We connect", d: "Marketing, sales and product sources flowing into BigQuery." },
          { n: "02", t: "We model", d: "Clean, joined data, ready to analyze." },
          { n: "03", t: "We define", d: "Certified metrics and a shared semantic layer." },
          { n: "04", t: "We visualize", d: "Power BI and Looker dashboards used every day." },
          { n: "05", t: "We drive adoption", d: "Training, rituals and support so the change sticks." },
        ],
        closing: { title: "One number. One truth.", body: "A BigQuery data warehouse, governed metrics and dashboards with real adoption.", cta: "Unify my reporting" },
      },
    },
  },
  {
    slug: "advanced-modeling",
    icon: "BrainCircuit",
    es: {
      name: "Modelado Avanzado",
      tagline: "Clustering, scoring, MMM y uplift: ciencia de datos aplicada al crecimiento.",
      intro: "Los modelos predictivos dejaron de ser cosa de grandes corporaciones. Segmentamos, puntuamos y predecimos con tus propios datos para invertir donde más retorna.",
      blocks: [
        {
          h: "Segmentación & clustering",
          p: "Descubre los segmentos que de verdad existen en tu base de clientes — no los que supone el departamento de marketing — y actívalos en tus canales.",
          list: ["Clustering sobre comportamiento real", "Personas basadas en datos", "Activable en CRM y paid media"],
        },
        {
          h: "Scoring & propensión",
          p: "Prioriza leads y clientes por probabilidad de compra, churn o crecimiento. Tu equipo ataca primero donde más retorna.",
          list: ["Lead scoring & propensión de compra", "Predicción de churn", "Priorización de cartera por potencial"],
        },
        {
          h: "MMM & atribución",
          p: "¿Qué canal merece el próximo euro? Marketing Mix Modeling y atribución multi-touch para decidir con evidencia, no con intuición.",
          list: ["Marketing Mix Modeling (MMM)", "Atribución multi-touch & data-driven", "Simuladores de inversión por canal"],
        },
        {
          h: "Uplift & potential growth",
          p: "No basta con predecir quién comprará: identificamos a quién convencerá tu acción de marketing y cuánto potencial de crecimiento tiene cada cliente.",
          list: ["Uplift modeling para campañas", "Potential growth por cliente", "Test geo & medición de lift"],
        },
      ],
      outcomes: ["Presupuesto invertido donde más retorna", "Leads priorizados por probabilidad real", "Campañas dirigidas a quien de verdad convence", "Modelos en producción, no en un cajón"],
    },
    en: {
      name: "Advanced Modeling",
      tagline: "Clustering, scoring, MMM and uplift: data science applied to growth.",
      intro: "Predictive modeling is no longer just for large corporations. We segment, score and predict with your own data so you invest where returns are highest.",
      blocks: [
        {
          h: "Segmentation & clustering",
          p: "Discover the segments that truly exist in your customer base — not the ones marketing assumes — and activate them across your channels.",
          list: ["Clustering on real behavior", "Data-driven personas", "Activatable in CRM and paid media"],
        },
        {
          h: "Scoring & propensity",
          p: "Prioritize leads and customers by probability of purchase, churn or growth. Your team strikes first where returns are highest.",
          list: ["Lead scoring & purchase propensity", "Churn prediction", "Portfolio prioritization by potential"],
        },
        {
          h: "MMM & attribution",
          p: "Which channel deserves the next euro? Marketing Mix Modeling and multi-touch attribution to decide with evidence, not intuition.",
          list: ["Marketing Mix Modeling (MMM)", "Multi-touch & data-driven attribution", "Per-channel investment simulators"],
        },
        {
          h: "Uplift & potential growth",
          p: "Predicting who will buy isn't enough: we identify who your marketing will actually persuade and how much growth potential each customer holds.",
          list: ["Uplift modeling for campaigns", "Per-customer potential growth", "Geo tests & lift measurement"],
        },
      ],
      outcomes: ["Budget invested where it returns most", "Leads prioritized by real probability", "Campaigns aimed at those truly persuadable", "Models in production, not in a drawer"],
    },
    deep: {
      es: {
        kicker: "Ciencia de datos aplicada",
        title: "Modelos que terminan en tu CRM, no en un cajón",
        intro: "Un modelo que no se activa no vale nada. Construimos segmentación, scoring, MMM y uplift conectados a tus herramientas y medidos contra grupo de control.",
        caps: [
          { t: "Con tus datos", d: "Modelos entrenados con tu comportamiento real de cliente, no con benchmarks genéricos." },
          { t: "Explicables", d: "Cada predicción viene con el porqué. Negocio entiende y confía en el modelo." },
          { t: "Activados", d: "El scoring llega a tu CRM y la segmentación a tus campañas. Producción, no PowerPoint." },
          { t: "Medidos", d: "Impacto real contra grupo de control y re-entreno continuo del modelo." },
        ],
        visual: {
          label: "Modelos que dejamos en producción",
          controls: ["Segmentación & clustering", "Lead scoring & propensión de compra", "Predicción de churn", "MMM & atribución multi-touch", "Uplift modeling", "Potential growth por cliente"],
          stat: "+22%",
          statLabel: "Uplift medio en campañas optimizadas",
        },
        steps: [
          { n: "01", t: "Entendemos", d: "El negocio, los datos disponibles y la decisión que hay que mejorar." },
          { n: "02", t: "Exploramos", d: "Calidad del dato, variables y señal predictiva real." },
          { n: "03", t: "Modelamos", d: "Validación rigurosa y explicabilidad ante negocio." },
          { n: "04", t: "Activamos", d: "El modelo conectado a CRM, paid media o BI." },
          { n: "05", t: "Medimos", d: "Impacto contra grupo de control y re-entreno continuo." },
        ],
        closing: { title: "Predecir bien es vender antes.", body: "Segmentación, scoring, MMM y uplift en producción, medidos contra grupo de control.", cta: "Quiero predecir" },
      },
      en: {
        kicker: "Applied data science",
        title: "Models that end up in your CRM, not in a drawer",
        intro: "A model that never gets activated is worthless. We build segmentation, scoring, MMM and uplift connected to your tools and measured against a control group.",
        caps: [
          { t: "With your data", d: "Models trained on your real customer behavior, not generic benchmarks." },
          { t: "Explainable", d: "Every prediction comes with its why. Business understands and trusts the model." },
          { t: "Activated", d: "Scoring lands in your CRM and segmentation in your campaigns. Production, not PowerPoint." },
          { t: "Measured", d: "Real impact against a control group and continuous model retraining." },
        ],
        visual: {
          label: "Models we leave running in production",
          controls: ["Segmentation & clustering", "Lead scoring & purchase propensity", "Churn prediction", "MMM & multi-touch attribution", "Uplift modeling", "Per-customer potential growth"],
          stat: "+22%",
          statLabel: "Average uplift in optimized campaigns",
        },
        steps: [
          { n: "01", t: "We understand", d: "The business, the available data and the decision to improve." },
          { n: "02", t: "We explore", d: "Data quality, variables and real predictive signal." },
          { n: "03", t: "We model", d: "Rigorous validation and explainability for business stakeholders." },
          { n: "04", t: "We activate", d: "The model connected to CRM, paid media or BI." },
          { n: "05", t: "We measure", d: "Impact against a control group and continuous retraining." },
        ],
        closing: { title: "Predicting well means selling sooner.", body: "Segmentation, scoring, MMM and uplift in production, measured against a control group.", cta: "I want to predict" },
      },
    },
  },
];
